import { Link } from 'react-router-dom';
import { ROUTES } from '@traction/shared';
import { Search, PenTool, Mail, BarChart3, Shield, Zap } from 'lucide-react';

const features = [
  {
    icon: Search,
    title: 'Scout Agent',
    description: 'AI-powered lead generation that runs overnight. Scout queries Apollo.io and monitors buying signals to find ICP-matching prospects.',
    highlights: ['ICP-based sourcing', 'Signal detection', 'LinkedIn data mining', 'Lead deduplication'],
  },
  {
    icon: PenTool,
    title: 'Scribe Agent',
    description: 'AI copywriter that crafts hyper-personalized emails. Scribe researches each prospect and writes emails that feel human.',
    highlights: ['Profile research', 'Personalized drafts', 'Tone matching', 'Style learning'],
  },
  {
    icon: Mail,
    title: 'Gmail Integration',
    description: 'Chrome extension that brings leads directly into your inbox. Review, edit, and send without leaving Gmail.',
    highlights: ['Native Gmail sidebar', 'One-click compose', 'Send tracking', 'Reply detection'],
  },
  {
    icon: BarChart3,
    title: 'Lead Dashboard',
    description: 'Unified view of your pipeline. Filter leads, track engagement, and monitor what is working.',
    highlights: ['Lead queue', 'Smart filters', 'Engagement history', 'Performance analytics'],
  },
  {
    icon: Shield,
    title: 'Smart Compliance',
    description: 'Built-in safeguards to keep your outreach compliant and professional.',
    highlights: ['Do Not Contact list', 'Unsubscribe handling', 'Rate limiting', 'Domain warming'],
  },
  {
    icon: Zap,
    title: 'Intent Detection',
    description: 'AI classifies incoming replies so you know which prospects are warm and ready to engage.',
    highlights: ['Reply classification', 'Priority alerts', 'Follow-up suggestions', 'Warm lead flagging'],
  },
];

export function FeaturesPage() {
  return (
    <div className="py-12 bg-cacao-dark">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h1 className="font-heading text-xl font-semibold text-[#F5F5F5]">Features</h1>
          <p className="mt-2 text-sm text-cacao-muted max-w-lg mx-auto">
            Everything you need to generate leads and send personalized outreach at scale
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {features.map((feature) => (
            <div
              key={feature.title}
              className="rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary p-5 hover:border-cacao-dark-elevated transition-colors"
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-md bg-cacao-dark-tertiary">
                <feature.icon className="h-4 w-4 text-cacao-gold" />
              </div>
              <h3 className="mt-3 text-sm font-medium text-[#F5F5F5]">{feature.title}</h3>
              <p className="mt-1.5 text-sm text-cacao-muted leading-relaxed">{feature.description}</p>
              <ul className="mt-3 space-y-1.5">
                {feature.highlights.map((highlight) => (
                  <li key={highlight} className="flex items-center text-xs text-cacao-text-tertiary">
                    <span className="mr-1.5 h-1 w-1 rounded-full bg-cacao-gold" />
                    {highlight}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <Link
            to={ROUTES.SIGNUP}
            className="inline-block rounded-md bg-cacao-gold px-4 py-2 text-sm font-medium text-cacao-dark hover:bg-cacao-gold-hover transition-colors"
          >
            Start free trial
          </Link>
          <p className="mt-3 text-xs text-cacao-text-tertiary">
            14-day free trial. No credit card required.
          </p>
        </div>
      </div>
    </div>
  );
}
